import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { addVimeoMetadataToCollectionItems, ContentCollection } from './useContentCollection';
import { getSessionSeed } from '../../utils/sessionSeed';
import { fetchVimeoMetadataBatch, addVimeoMetadataToExperiences } from './useBrowseExperiences';
import { Content } from './useExperiences';

const domain = process.env.REACT_APP_DOMAIN_NAME;
const apiVersion3 = process.env.REACT_APP_API_VERSION3;

const getPublicContentCollection = async (publicKey: string, clientId?: number): Promise<ContentCollection> => {
  const params: Record<string, string | number> = { seed: getSessionSeed() };
  if (clientId) {
    params.client_id = clientId;
  }

  const response = await axios.get(`${domain}${apiVersion3}/public/content-collections/${publicKey}`, {
    params,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHTTPRequest',
    },
  });

  const data: ContentCollection = response.data;
  data.items = await addVimeoMetadataToCollectionItems(data.items);

  const videoIds = Array.from(
    new Set(data.items.map((item) => (item.data as Content)?.video_id).filter((id): id is string => Boolean(id))),
  );
  if (videoIds.length > 0) {
    const metadataMap = await fetchVimeoMetadataBatch(videoIds);
    data.items = data.items.map((item) => {
      const content = item.data as Content;
      if (content?.video_id) {
        return { ...item, data: addVimeoMetadataToExperiences(content, metadataMap) };
      }
      return item;
    });
  }

  return data;
};

export const usePublicContentCollection = (publicKey?: string, clientId?: number) => {
  return useQuery<ContentCollection>(
    ['publicContentCollection', publicKey, clientId],
    () => getPublicContentCollection(publicKey!, clientId),
    {
      enabled: !!publicKey,
      retry: false,
    },
  );
};
